import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { saveMindMap, selectCurrentMindMap } from './slice/mindSlice2';
import './MindMap2.css';

// Import template files
import DesignProcessTemplate from './templates/DesignProcess.json';
import ProblemStatementTemplate from './templates/ProblemStatementTemplate.json';
import BrainStormingTemplate from './templates/BrainStormingBootcamp.json';
import WorkBreakDownTemplate from './templates/WorkBreakDownStructure.json';
import StakeholderAnalysisTemplate from './templates/StakeholderAnalysisTemplate.json';
import MorphologicalAnalysisTemplate from './templates/MorphologicalAnalysisTemplate.json';
import LinearResponsibilityTemplate from './templates/LinearResponsibilityTemplate.json';
import GanttChartTemplate from './templates/GanttChartTemplate.json';
import DesignSpecTemplate from './templates/designSpecification.json';
import DecisionMatrixTemplate from './templates/DesicionMatrixTemplate.json';
import BlackboxAnalysisTemplate from './templates/BlackboxAnalysisTemplate.json';
import CollectingInfoTemplate from './templates/CollectingImportantInformationTemplate.json';
import FullDesignProcessTemplate from './templates/FullDesignProcess.json';

const templates = [
    { id: 'design-process', name: 'Design Process', data: DesignProcessTemplate },
    { id: 'full-design-process', name: 'Full Design Process', data: FullDesignProcessTemplate },
    { id: 'problem-statement', name: 'Problem Statement', data: ProblemStatementTemplate },
    { id: 'brainstorming', name: 'Brainstorming Bootcamp', data: BrainStormingTemplate },
    { id: 'wbs', name: 'Work Breakdown Structure', data: WorkBreakDownTemplate },
    { id: 'stakeholder', name: 'Stakeholder Analysis', data: StakeholderAnalysisTemplate },
    { id: 'morphological', name: 'Morphological Analysis', data: MorphologicalAnalysisTemplate },
    { id: 'linear-responsibility', name: 'Linear Responsibility Chart', data: LinearResponsibilityTemplate },
    { id: 'gantt', name: 'Gantt Chart', data: GanttChartTemplate },
    { id: 'design-spec', name: 'Design Specification', data: DesignSpecTemplate },
    { id: 'decision-matrix', name: 'Decision Matrix', data: DecisionMatrixTemplate },
    { id: 'blackbox', name: 'Blackbox Analysis', data: BlackboxAnalysisTemplate },
    { id: 'collecting-info', name: 'Collecting Information', data: CollectingInfoTemplate }
];

// Example mind maps for different use cases
const exampleMindMaps = {
    brainstorming: [
        { id: 'root', text: 'Product Ideas', x: 150, y: 80, shape: 'circle', width: 100, height: 50, children: [] },
        { id: 'c1', text: 'Features', x: 80, y: 150, shape: 'square', width: 80, height: 40, children: [] },
        { id: 'c2', text: 'Market', x: 150, y: 150, shape: 'square', width: 70, height: 40, children: [] },
        { id: 'c3', text: 'Users', x: 220, y: 150, shape: 'square', width: 70, height: 40, children: [] }
    ],
    problemSolving: [
        { id: 'root', text: 'Problem', x: 150, y: 80, shape: 'hexagon', width: 90, height: 60, children: [] },
        { id: 'c1', text: 'Cause 1', x: 80, y: 160, shape: 'circle', width: 70, height: 40, children: [] },
        { id: 'c2', text: 'Cause 2', x: 150, y: 160, shape: 'circle', width: 70, height: 40, children: [] },
        { id: 'c3', text: 'Solution', x: 220, y: 160, shape: 'square', width: 80, height: 40, children: [] }
    ],
    studying: [
        { id: 'root', text: 'Topic', x: 150, y: 80, shape: 'circle', width: 80, height: 50, children: [] },
        { id: 'c1', text: 'Key Point', x: 70, y: 150, shape: 'square', width: 75, height: 40, children: [] },
        { id: 'c2', text: 'Example', x: 150, y: 150, shape: 'hexagon', width: 75, height: 50, children: [] },
        { id: 'c3', text: 'Summary', x: 230, y: 150, shape: 'square', width: 75, height: 40, children: [] }
    ],
    projectPlanning: [
        { id: 'root', text: 'Project', x: 150, y: 80, shape: 'square', width: 80, height: 50, children: [] },
        { id: 'c1', text: 'Phase 1', x: 80, y: 150, shape: 'circle', width: 70, height: 40, children: [] },
        { id: 'c2', text: 'Phase 2', x: 150, y: 150, shape: 'circle', width: 70, height: 40, children: [] },
        { id: 'c3', text: 'Goal', x: 220, y: 150, shape: 'hexagon', width: 70, height: 50, children: [] }
    ],
    writing: [
        { id: 'root', text: 'Essay', x: 150, y: 80, shape: 'circle', width: 80, height: 50, children: [] },
        { id: 'c1', text: 'Intro', x: 70, y: 150, shape: 'square', width: 60, height: 40, children: [] },
        { id: 'c2', text: 'Body', x: 150, y: 150, shape: 'square', width: 60, height: 40, children: [] },
        { id: 'c3', text: 'Conclusion', x: 230, y: 150, shape: 'hexagon', width: 85, height: 50, children: [] }
    ]
};

const MindMapExample = ({ nodes }) => {
    const renderNode = (node) => {
        const shape = node.shape || 'circle';
        const width = node.width || 80;
        const height = node.height || 40;

        return (
            <g key={node.id}>
                <foreignObject
                    x={node.x - width / 2}
                    y={node.y - height / 2}
                    width={width}
                    height={height}
                >
                    <div
                        className={`mind-node-example mind-node-${shape} ${node.id === 'root' ? 'root-node' : ''}`}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <div className="node-text">{node.text}</div>
                    </div>
                </foreignObject>
            </g>
        );
    };

    const renderConnections = () => {
        const lines = [];
        const root = nodes[0];
        nodes.slice(1).forEach(child => {
            lines.push(
                <line
                    key={`line-${root.id}-${child.id}`}
                    x1={root.x}
                    y1={root.y}
                    x2={child.x}
                    y2={child.y}
                    stroke="#753bbd"
                    strokeWidth="2"
                />
            );
        });
        return lines;
    };

    return (
        <svg width="300" height="200" className="example-mindmap">
            {renderConnections()}
            {nodes.map(node => renderNode(node))}
        </svg>
    );
};

const MindMap2 = ({ currentMindMap, saveMindMap }) => {
    const [nodes, setNodes] = useState(currentMindMap?.nodes || [
        { id: 'root', text: 'Root Idea', x: 1007, y: 776, children: [], shape: 'circle', width: 150, height: 60 }
    ]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [isPanning, setIsPanning] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [editingNode, setEditingNode] = useState(null);
    const [zoom, setZoom] = useState(1.5);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showShapeMenu, setShowShapeMenu] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [resizing, setResizing] = useState(null);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const canvasRef = useRef(null);
    const hasLoadedRef = useRef(false);
    const mindMapId = 'default';

    // Save to Redux whenever nodes change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            saveMindMap({
                id: mindMapId,
                nodes: nodes
            });
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [nodes, saveMindMap]);

    // Load from Redux on mount
    useEffect(() => {
        if (currentMindMap?.nodes && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            // Check if root node needs repositioning to center
            const loadedNodes = currentMindMap.nodes;
            const rootNode = loadedNodes.find(n => n.id === 'root');
            if (rootNode && (rootNode.x < 900 || rootNode.y < 700)) {
                // Reposition old root node to center
                const updatedNodes = loadedNodes.map(n =>
                    n.id === 'root' ? { ...n, x: 1007, y: 776 } : n
                );
                setNodes(updatedNodes);
            } else {
                setNodes(loadedNodes);
            }
        }
    }, [currentMindMap]);

    const findNode = (id, nodeList = nodes) => {
        for (let node of nodeList) {
            if (node.id === id) return node;
            if (node.children && node.children.length > 0) {
                const found = findNode(id, node.children);
                if (found) return found;
            }
        }
        return null;
    };

    const updateNode = (id, updates) => {
        const updateInTree = (nodeList) => {
            return nodeList.map(node => {
                if (node.id === id) {
                    return { ...node, ...updates };
                }
                if (node.children && node.children.length > 0) {
                    return { ...node, children: updateInTree(node.children) };
                }
                return node;
            });
        };
        setNodes(updateInTree(nodes));
    };

    const addChild = (parentId, shape = 'circle') => {
        // Set appropriate default sizes based on shape
        let defaultWidth = 150;
        let defaultHeight = 60;

        if (shape === 'parallelogram' || shape === 'trapezoid') {
            defaultWidth = 180; // Wider for angled shapes
        } else if (shape === 'pentagon') {
            defaultWidth = 160;
            defaultHeight = 70;
        } else if (shape === 'cross') {
            defaultWidth = 120;
            defaultHeight = 120; // Square aspect ratio for cross
        } else if (shape === 'hexagon') {
            defaultWidth = 170;
        }

        const newNode = {
            id: `node-${Date.now()}`,
            text: 'New Node',
            x: 0,
            y: 0,
            children: [],
            shape: shape,
            width: defaultWidth,
            height: defaultHeight,
            parentId: parentId
        };

        const addToTree = (nodeList) => {
            return nodeList.map(node => {
                if (node.id === parentId) {
                    const childCount = node.children.length;
                    const angle = (Math.PI * 2 * childCount) / (childCount + 1);
                    newNode.x = node.x + Math.cos(angle) * 150;
                    newNode.y = node.y + Math.sin(angle) * 150;
                    return {
                        ...node,
                        children: [...node.children, newNode]
                    };
                }
                if (node.children && node.children.length > 0) {
                    return { ...node, children: addToTree(node.children) };
                }
                return node;
            });
        };
        setNodes(addToTree(nodes));
        setShowShapeMenu(false);
    };

    const deleteNode = (id) => {
        if (id === 'root') return; // Don't delete root

        const deleteFromTree = (nodeList) => {
            return nodeList.filter(node => {
                if (node.id === id) return false;
                if (node.children && node.children.length > 0) {
                    node.children = deleteFromTree(node.children);
                }
                return true;
            }).map(node => {
                if (node.children && node.children.length > 0) {
                    return { ...node, children: deleteFromTree(node.children) };
                }
                return node;
            });
        };
        setNodes(deleteFromTree(nodes));
        if (selectedNode === id) setSelectedNode(null);
    };

    const handleMouseDown = (e, nodeId) => {
        if (e.button !== 0) return; // Only left click
        e.stopPropagation(); // Prevent canvas panning when clicking on node
        const node = findNode(nodeId);
        if (node) {
            setDragging(nodeId);
            setSelectedNode(nodeId);
            setOffset({
                x: e.clientX - node.x * zoom,
                y: e.clientY - node.y * zoom
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isPanning) {
            const deltaX = e.clientX - offset.x;
            const deltaY = e.clientY - offset.y;
            setPan({ x: pan.x + deltaX, y: pan.y + deltaY });
            setOffset({ x: e.clientX, y: e.clientY });
        } else if (dragging) {
            const newX = (e.clientX - offset.x) / zoom;
            const newY = (e.clientY - offset.y) / zoom;
            updateNode(dragging, { x: newX, y: newY });
        } else if (resizing) {
            const deltaX = (e.clientX - resizeStart.x);
            const deltaY = (e.clientY - resizeStart.y);
            const newWidth = Math.max(80, resizeStart.width + deltaX);
            const newHeight = Math.max(40, resizeStart.height + deltaY);
            updateNode(resizing, { width: newWidth, height: newHeight });
        }
    };

    const handleMouseUp = () => {
        setDragging(null);
        setIsPanning(false);
        setResizing(null);
    };

    const handleResizeStart = (e, nodeId) => {
        e.stopPropagation();
        const node = findNode(nodeId);
        if (node) {
            setResizing(nodeId);
            setResizeStart({
                x: e.clientX,
                y: e.clientY,
                width: node.width || 150,
                height: node.height || 60
            });
        }
    };

    const handleDoubleClick = (nodeId) => {
        setEditingNode(nodeId);
    };

    const handleTextChange = (e, nodeId) => {
        updateNode(nodeId, { text: e.target.value });
    };

    const handleTextBlur = () => {
        setEditingNode(null);
    };

    const convertTemplateToNodes = (templateData) => {
        const topics = templateData.topics;
        const rootTopic = topics.find(t => t.key === 'root');

        // Helper function to estimate node size based on text length
        const calculateNodeSize = (text, isRoot = false) => {
            const baseCharWidth = 8; // approximate pixels per character
            const minWidth = isRoot ? 180 : 140;
            const maxWidth = isRoot ? 250 : 200;
            const estimatedWidth = Math.min(maxWidth, Math.max(minWidth, text.length * baseCharWidth + 30));
            const height = isRoot ? 70 : 60;
            return { width: estimatedWidth, height };
        };

        const convertedNodes = [];
        const rootX = 1007;
        const rootY = 776;

        // Helper function to find topic by key
        const findTopic = (key) => topics.find(t => t.key === key);

        // Recursive function to create nodes and their children
        const createNodeWithChildren = (topic, parentX, parentY, level = 0, angleOffset = 0, parentAngleSpan = Math.PI * 2) => {
            const isRoot = topic.key === 'root';
            const text = topic.blocks[0].data;
            const size = calculateNodeSize(text, isRoot);

            // Calculate position
            let x, y;
            if (isRoot) {
                x = rootX;
                y = rootY;
            } else {
                // Position children in a circular pattern around parent
                const radius = 200 + (level - 1) * 150; // Increase radius for deeper levels
                x = parentX;
                y = parentY;
            }

            const node = {
                id: topic.key,
                text: text,
                x: x,
                y: y,
                shape: isRoot ? 'circle' : (level === 1 ? (convertedNodes.length % 3 === 0 ? 'circle' : (convertedNodes.length % 3 === 1 ? 'square' : 'hexagon')) : 'square'),
                width: size.width,
                height: size.height,
                children: []
            };

            return node;
        };

        // Create root node
        const rootText = rootTopic.blocks[0].data;
        const rootSize = calculateNodeSize(rootText, true);
        const rootNode = {
            id: 'root',
            text: rootText,
            x: rootX,
            y: rootY,
            shape: 'circle',
            width: rootSize.width,
            height: rootSize.height,
            children: []
        };
        convertedNodes.push(rootNode);

        // Get all direct children of root and their descendants - pyramid layout with staggered rows
        const processChildren = (parentKey, parentX, parentY, level = 1) => {
            const children = topics.filter(t => t.parentKey === parentKey && t.key !== 'root');

            // Calculate horizontal spacing based on number of children
            const horizontalSpacing = 300;
            const verticalSpacing = 180;
            const rowOffset = 100; // Offset for alternating rows

            // For sub-children (level > 1), use two-row staggered layout
            const useStaggered = level > 1;
            const totalWidth = (children.length - 1) * horizontalSpacing;
            const startX = parentX - totalWidth / 2;

            children.forEach((topic, index) => {
                const childText = topic.blocks[0].data;
                const childSize = calculateNodeSize(childText, false);

                // Determine row position for staggered layout
                const rowIndex = useStaggered ? index % 2 : 0;
                const colIndex = useStaggered ? Math.floor(index / 2) : index;
                const rowSpacing = useStaggered ? horizontalSpacing * 2 : horizontalSpacing;
                const yOffset = rowIndex * rowOffset;

                const childNode = {
                    id: topic.key,
                    text: childText,
                    x: useStaggered
                        ? parentX - ((children.length / 2 - 1) * rowSpacing) / 2 + colIndex * rowSpacing
                        : startX + index * horizontalSpacing,
                    y: parentY + verticalSpacing + yOffset,
                    shape: level === 1 ? (index % 3 === 0 ? 'circle' : (index % 3 === 1 ? 'square' : 'hexagon')) : 'square',
                    width: childSize.width,
                    height: childSize.height,
                    children: [],
                    parentId: parentKey
                };
                convertedNodes.push(childNode);

                // Recursively process grandchildren
                if (topic.subKeys && topic.subKeys.length > 0) {
                    processChildren(topic.key, childNode.x, childNode.y, level + 1);
                }
            });
        };

        processChildren('root', rootX, rootY, 1);

        return convertedNodes;
    };

    const loadTemplate = (template) => {
        const convertedNodes = convertTemplateToNodes(template.data);
        setNodes(convertedNodes);
        setShowTemplateModal(false);
        setSelectedNode(null);
    };

    const renderConnections = (node, parentNode = null) => {
        const lines = [];
        if (parentNode) {
            lines.push(
                <line
                    key={`line-${parentNode.id}-${node.id}`}
                    x1={parentNode.x}
                    y1={parentNode.y}
                    x2={node.x}
                    y2={node.y}
                    stroke="#753bbd"
                    strokeWidth="2"
                />
            );
        }
        if (node.children && node.children.length > 0) {
            node.children.forEach(child => {
                lines.push(...renderConnections(child, node));
            });
        }
        return lines;
    };

    const renderNodes = (nodeList) => {
        return nodeList.map(node => {
            const shape = node.shape || 'circle';
            const width = node.width || 150;
            const height = node.height || 60;

            return (
                <g key={node.id}>
                    <foreignObject
                        x={node.x - width / 2}
                        y={node.y - height / 2}
                        width={width}
                        height={height}
                        onMouseDown={(e) => handleMouseDown(e, node.id)}
                        onDoubleClick={() => handleDoubleClick(node.id)}
                    >
                        <div
                            className={`mind-node mind-node-${shape} ${selectedNode === node.id ? 'selected' : ''} ${node.id === 'root' ? 'root-node' : ''}`}
                            style={{ width: '100%', height: '100%' }}
                        >
                            {editingNode === node.id ? (
                                <input
                                    type="text"
                                    value={node.text}
                                    onChange={(e) => handleTextChange(e, node.id)}
                                    onBlur={handleTextBlur}
                                    autoFocus
                                    className="node-input"
                                />
                            ) : (
                                <div className="node-text">{node.text}</div>
                            )}
                        </div>
                    </foreignObject>
                    {/* Resize handle - only show when selected */}
                    {selectedNode === node.id && (
                        <circle
                            cx={node.x + width / 2}
                            cy={node.y + height / 2}
                            r="6"
                            fill="#2196F3"
                            stroke="white"
                            strokeWidth="2"
                            style={{ cursor: 'nwse-resize' }}
                            onMouseDown={(e) => handleResizeStart(e, node.id)}
                        />
                    )}
                    {node.children && node.children.length > 0 && renderNodes(node.children)}
                </g>
            );
        });
    };

    const exportToJSON = () => {
        const dataStr = JSON.stringify({ nodes }, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'mindmap.json';
        link.click();
        URL.revokeObjectURL(url);
    };

    const importFromJSON = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const data = JSON.parse(event.target.result);
                    if (data.nodes) {
                        setNodes(data.nodes);
                    }
                } catch (err) {
                    console.error('Error parsing JSON:', err);
                    alert('Invalid JSON file');
                }
            };
            reader.readAsText(file);
        }
    };

    const getAllConnections = () => {
        const connections = [];

        // For flat structure, draw lines based on parentId
        nodes.forEach(node => {
            if (node.parentId) {
                const parent = nodes.find(n => n.id === node.parentId);
                if (parent) {
                    connections.push(
                        <line
                            key={`line-${parent.id}-${node.id}`}
                            x1={parent.x}
                            y1={parent.y}
                            x2={node.x}
                            y2={node.y}
                            stroke="#753bbd"
                            strokeWidth="2"
                        />
                    );
                }
            }
        });

        return connections;
    };

    return (
        <div className="mindmap2-container">
            <div className="mindmap2-toolbar">
                <div className="add-node-dropdown">
                    <button
                        onClick={() => setShowShapeMenu(!showShapeMenu)}
                        disabled={!selectedNode}
                        className="add-node-btn"
                    >
                        + Add Child Node ▼
                    </button>
                    {showShapeMenu && selectedNode && (
                        <div className="shape-menu">
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'circle')}>
                                <div className="shape-preview shape-preview-circle"></div>
                                <span>Circle</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'square')}>
                                <div className="shape-preview shape-preview-square"></div>
                                <span>Square</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'hexagon')}>
                                <div className="shape-preview shape-preview-hexagon"></div>
                                <span>Hexagon</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'parallelogram')}>
                                <div className="shape-preview shape-preview-parallelogram"></div>
                                <span>Parallelogram</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'trapezoid')}>
                                <div className="shape-preview shape-preview-trapezoid"></div>
                                <span>Trapezoid</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'pentagon')}>
                                <div className="shape-preview shape-preview-pentagon"></div>
                                <span>Pentagon</span>
                            </div>
                            <div className="shape-option" onClick={() => addChild(selectedNode, 'cross')}>
                                <div className="shape-preview shape-preview-cross"></div>
                                <span>Cross</span>
                            </div>
                        </div>
                    )}
                </div>
                <button onClick={() => selectedNode && selectedNode !== 'root' && deleteNode(selectedNode)}
                    disabled={!selectedNode || selectedNode === 'root'}>
                    🗑️ Delete Node
                </button>
                <button onClick={() => setZoom(z => Math.min(z + 0.1, 2))}>🔍 Zoom In</button>
                <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}>🔍 Zoom Out</button>
                <button onClick={exportToJSON}>💾 Export JSON</button>
                <label className="import-btn">
                    📂 Import JSON
                    <input type="file" accept=".json" onChange={importFromJSON} style={{ display: 'none' }} />
                </label>
                <button onClick={() => setShowTemplateModal(true)} className="template-btn" title="Load template">
                    📋
                </button>
                <button onClick={() => setShowHelpModal(true)} className="help-btn" title="Mind mapping tips">
                    ❓
                </button>
                <span className="toolbar-info">
                    {selectedNode ? `Selected: ${findNode(selectedNode)?.text}` : 'Select a node'}
                </span>
            </div>

            <div
                className="mindmap2-canvas"
                onMouseDown={(e) => {
                    // Start panning with left-click on empty space (not on a node)
                    if (e.button === 0 && e.target.tagName === 'svg') {
                        setIsPanning(true);
                        setOffset({ x: e.clientX, y: e.clientY });
                    }
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <svg
                    ref={canvasRef}
                    width="100%"
                    height="100%"
                    viewBox="0 0 2400 1600"
                    style={{ transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`, transformOrigin: 'center center' }}
                >
                    {getAllConnections()}
                    {renderNodes(nodes)}
                </svg>
            </div>

            {/* Help Modal */}
            {showHelpModal && (
                <div className="mindmap-help-modal-overlay" onClick={() => setShowHelpModal(false)}>
                    <div className="mindmap-help-modal" onClick={e => e.stopPropagation()}>
                        <div className="mindmap-help-header">
                            <h3>❓ Mind Map Help & Instructions</h3>
                            <button onClick={() => setShowHelpModal(false)} className="help-close-btn">×</button>
                        </div>
                        <div className="mindmap-help-content">
                            <div className="help-section">
                                <h4>📖 Basic Instructions</h4>
                                <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
                                    <li>Click and drag on empty space to pan the canvas</li>
                                    <li>Click to select a node</li>
                                    <li>Drag nodes to move them around</li>
                                    <li>Double-click a node to edit its text</li>
                                    <li>Use toolbar buttons to add/delete nodes</li>
                                    <li>Choose different shapes when adding child nodes</li>
                                    <li>Drag the blue circle on selected nodes to resize</li>
                                    <li>Use zoom buttons to zoom in/out</li>
                                </ul>
                            </div>
                            <div className="help-section">
                                <h4>🧠 Mind Mapping Use Cases</h4>
                            </div>
                            <div className="help-section">
                                <h4>📝 Brainstorming Ideas</h4>
                                <p>Capture thoughts freely and organize them visually as they connect to your main topic.</p>
                                <MindMapExample nodes={exampleMindMaps.brainstorming} />
                            </div>
                            <div className="help-section">
                                <h4>🎯 Problem Solving</h4>
                                <p>Break down complex problems into smaller components and explore different solution paths.</p>
                                <MindMapExample nodes={exampleMindMaps.problemSolving} />
                            </div>
                            <div className="help-section">
                                <h4>📚 Study & Learning</h4>
                                <p>Map out concepts and their relationships to better understand and remember material.</p>
                                <MindMapExample nodes={exampleMindMaps.studying} />
                            </div>
                            <div className="help-section">
                                <h4>🗂️ Project Planning</h4>
                                <p>Outline project tasks, dependencies, and timelines in a visual hierarchical structure.</p>
                                <MindMapExample nodes={exampleMindMaps.projectPlanning} />
                            </div>
                            <div className="help-section">
                                <h4>✍️ Writing & Research</h4>
                                <p>Organize your thoughts before writing or map out research topics and sources.</p>
                                <MindMapExample nodes={exampleMindMaps.writing} />
                            </div>
                            <div className="help-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px', borderLeft: '4px solid #9C27B0' }}>
                                <p style={{ margin: 0, fontSize: '14px' }}>
                                    <strong>💡 Tip:</strong> Looking for a quick start? Click the <strong>📋 Templates</strong> button in the toolbar to choose from pre-made mind map templates for common use cases like Design Process, Problem Solving, Project Planning, and more!
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Template Modal */}
            {showTemplateModal && (
                <div className="mindmap-help-modal-overlay" onClick={() => setShowTemplateModal(false)}>
                    <div className="mindmap-template-modal" onClick={e => e.stopPropagation()}>
                        <div className="mindmap-help-header">
                            <h3>📋 Mind Map Templates</h3>
                            <button onClick={() => setShowTemplateModal(false)} className="help-close-btn">×</button>
                        </div>
                        <div className="template-grid">
                            {templates.map(template => (
                                <div
                                    key={template.id}
                                    className="template-card"
                                    onClick={() => loadTemplate(template)}
                                >
                                    <div className="template-icon">📋</div>
                                    <h4>{template.name}</h4>
                                    <p>Click to load</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    currentMindMap: selectCurrentMindMap(state)
});

const mapDispatchToProps = {
    saveMindMap
};

export default connect(mapStateToProps, mapDispatchToProps)(MindMap2);
